
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const NetWorthCalculator = () => {
  // Assets
  const [cash, setCash] = useState<string>('10000');
  const [investments, setInvestments] = useState<string>('50000');
  const [realEstate, setRealEstate] = useState<string>('200000');
  const [vehicles, setVehicles] = useState<string>('15000');
  const [otherAssets, setOtherAssets] = useState<string>('5000');

  // Liabilities
  const [mortgage, setMortgage] = useState<string>('150000');
  const [creditCards, setCreditCards] = useState<string>('3000');
  const [loans, setLoans] = useState<string>('20000');
  const [otherDebts, setOtherDebts] = useState<string>('2000');

  // Results
  const [totalAssets, setTotalAssets] = useState<number | null>(null);
  const [totalLiabilities, setTotalLiabilities] = useState<number | null>(null);
  const [netWorth, setNetWorth] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateNetWorth = () => {
    try {
      setError(null);
      
      const assets = [cash, investments, realEstate, vehicles, otherAssets]
        .map(val => parseFloat(val) || 0);
      
      const liabilities = [mortgage, creditCards, loans, otherDebts]
        .map(val => parseFloat(val) || 0);

      const totalAssetValue = assets.reduce((sum, val) => sum + val, 0);
      const totalLiabilityValue = liabilities.reduce((sum, val) => sum + val, 0);
      const netWorthValue = totalAssetValue - totalLiabilityValue;

      setTotalAssets(totalAssetValue);
      setTotalLiabilities(totalLiabilityValue);
      setNetWorth(netWorthValue);

    } catch (err) {
      setError("Error calculating net worth");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Tabs defaultValue="calculator">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="calculator">Calculator</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
        </TabsList>

        <TabsContent value="calculator">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-green-600">Assets</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cash">Cash & Savings ($)</Label>
                    <Input
                      id="cash"
                      type="number"
                      value={cash}
                      onChange={(e) => setCash(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="investments">Investments ($)</Label>
                    <Input
                      id="investments"
                      type="number"
                      value={investments}
                      onChange={(e) => setInvestments(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="real-estate">Real Estate ($)</Label>
                    <Input
                      id="real-estate"
                      type="number"
                      value={realEstate}
                      onChange={(e) => setRealEstate(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vehicles">Vehicles ($)</Label>
                    <Input
                      id="vehicles"
                      type="number"
                      value={vehicles}
                      onChange={(e) => setVehicles(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="other-assets">Other Assets ($)</Label>
                    <Input
                      id="other-assets"
                      type="number"
                      value={otherAssets}
                      onChange={(e) => setOtherAssets(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-red-600">Liabilities</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="mortgage">Mortgage Balance ($)</Label>
                    <Input
                      id="mortgage"
                      type="number"
                      value={mortgage}
                      onChange={(e) => setMortgage(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="credit-cards">Credit Card Debt ($)</Label>
                    <Input
                      id="credit-cards"
                      type="number"
                      value={creditCards}
                      onChange={(e) => setCreditCards(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="loans">Loans ($)</Label>
                    <Input
                      id="loans"
                      type="number"
                      value={loans}
                      onChange={(e) => setLoans(e.target.value)}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Car loans, student loans, etc.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="other-debts">Other Debts ($)</Label>
                    <Input
                      id="other-debts"
                      type="number"
                      value={otherDebts}
                      onChange={(e) => setOtherDebts(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6">
            <Button onClick={calculateNetWorth} className="w-full">
              Calculate Net Worth
            </Button>

            {error && (
              <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="results">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-6">Net Worth Results</h2>
              
              {netWorth !== null ? (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="font-medium text-green-800">Total Assets</h3>
                      <p className="text-2xl font-bold text-green-600">
                        ${totalAssets?.toLocaleString()}
                      </p>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                      <h3 className="font-medium text-red-800">Total Liabilities</h3>
                      <p className="text-2xl font-bold text-red-600">
                        ${totalLiabilities?.toLocaleString()}
                      </p>
                    </div>

                    <div className={`p-4 rounded-lg ${netWorth >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                      <h3 className={`font-medium ${netWorth >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>
                        Net Worth
                      </h3>
                      <p className={`text-2xl font-bold ${netWorth >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                        ${netWorth.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium mb-3">Net Worth Analysis</h3>
                    {netWorth >= 0 ? (
                      <p className="text-green-600">
                        ✅ Positive net worth! Your assets exceed your liabilities.
                      </p>
                    ) : (
                      <p className="text-orange-600">
                        ⚠️ Negative net worth. Consider reducing debt or increasing assets.
                      </p>
                    )}
                    
                    <div className="mt-4 text-sm">
                      <p><strong>Tips to improve net worth:</strong></p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Pay down high-interest debt first</li>
                        <li>Increase savings and investments</li>
                        <li>Monitor and reduce unnecessary expenses</li>
                        <li>Consider additional income sources</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  Calculate your net worth to see detailed results here.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NetWorthCalculator;
